import { API } from "aws-amplify";
import { action, makeObservable, observable, flow, computed } from "mobx";
import {
  getUploadUrlFile,
  getUrlFile,
} from "../../graphql/queries";
import axios from "axios";
import TemplateEditorStore from "../components/templateEditor/TemplateEditorStore";
import {DefaultVariables, OriginVariables} from "../../utils/enums";

class MessageStore {
  isError = false;
  isLoading = false;

  id = "";
  template = "";
  body = "";
  payload = {};
  templateJson = null;

  updatingTemplate = false;

  s3FileRoute = "email_templates";

  defaultVariables = [];

  campaign = null;
  customer = null;
  modalStore = null;
  
  templateEditorStore = null;

  constructor({ customer, campaign, modalStore }) {
    if (!campaign || !campaign.metadata?.actions) {
      console.warn("Campaign actions are undefined; check initialization.");
    }

    makeObservable(this, {
      templateEditorStore: observable,
      isLoading: observable,
      isError: observable,
      payload: observable,
      templateJson: observable,
      updatingTemplate: observable,
      id: observable,
      template: observable,
      body: observable,
      s3FileRoute: observable,
      defaultVariables: observable,
      currentSubject: computed,
      updateGeneralField: action,
      showRequireFieldsMessage: action,
      getPayload: action,
      clearTemplate: action,
      loadPayload: action,
      setVariablesFromFile: action,
      uploadFileToS3: flow,
      saveTemplate: flow,
      loadTemplate: flow
    });

    this.templateEditorStore = new TemplateEditorStore();
    this.defaultVariables = DefaultVariables;

    this.campaign = campaign;
    this.customer = customer;
    this.modalStore = modalStore;

    //this.templateEditorStore.clear();
    this.templateEditorStore.updateField("to", "{{To}}");
    this.loadPayload();
  }

  get currentSubject() {
    return this.templateEditorStore.subject;
  }

  updateGeneralField(field, value) {
    this[field] = value;

    if (field === "sources") {
      this.templateEditorStore.updateField('sources', value);
    }
  }

  setVariablesFromFile({ variables, sources }){
    if (!Array.isArray(variables) || variables.length === 0) {
      return;
    }

    this.templateEditorStore.removeAllMappedVariables();
    this.templateEditorStore.updateField('sources', sources);

    const variablesToAdd = variables
      .filter((field) => field !== "email")
      .map((field) => ({
        name: field,
        variable: field,
        origin: OriginVariables.EVENT,
        isMapped: true,
        source: 'contacts',
        field,
      }));

    variablesToAdd.forEach((variable) => this.templateEditorStore.addVariable(variable));
  }

  async getPayload({ toDraft, toPublish }) {
    let tabComplete = true;
    const { html, design, variables, form } = this.templateEditorStore.exportData();

    if (!Array.isArray(variables)) {
      return { isComplete: false };
    }

    const bindingArray = variables.map((variable) => ({
      name: variable.name,
      variable: `{{${variable.variable}}}`,
      origin: variable.origin,
      value: variable.origin === OriginVariables.EVENT ? variable.field : variable.value,
      isMapped: variable.isMapped,
      isCustom: variable.isCustom,
      isDefault: variable.isDefault,
    }));

    if (design){
      await this.saveTemplate(html, design);
      this.templateJson = design;
      this.body = html;
    }

    const payloadArray = [
      { field: "from", value: form.from || "" },
      { field: "sender_name", value: form.senderName || "" },
      { field: "to", value: form.to || "" },
      { field: "subject", value: form.subject || "" },
      { field: "cc", value: form.cc || "" },
      { field: "bcc", value: form.bcc || "" },
      { field: "description", value: form.description || "" },
      { field: "body", value: design ? `${this.id}.html`: "" },
      { field: "template", value: design ? `${this.id}.json`: "" },
    ];

    this.payload = {
      binding: bindingArray,
      payload: payloadArray,
    };

    if (toDraft || toPublish) {
      if (!form.from || !form.to || !form.subject || !form.senderName){
        tabComplete = false;
      }
    }

    if (toPublish) {
      if(!design) {
        tabComplete = false;
      }
    }

    return {
      ...this.payload,
      isComplete: tabComplete,
    }
  }

  showRequireFieldsMessage(){
    this.templateEditorStore.showRequiredFieldsError();
  }

  loadPayload() {
    if (!this.campaign?.metadata?.actions?.length) {
      console.warn("No actions found in campaign metadata");
      return;
    }

    const action = this.campaign.metadata.actions[0];

    this.id = action.id;

    const payload = action?.payload;
    if (payload?.length) {
      payload.forEach((field) => {
         if (field.field === "body") {
          this.body = field.value;
        } else if (field.field === "template") {
          this.template = field.value;
        } else {
          this.templateEditorStore.updateField(field.field, field.value);
        }
      });
    }

    if (action?.binding?.length) {
      const variables = [...this.defaultVariables];
      action.binding.forEach((binding) => {
        binding.variable = binding.variable.replaceAll("{", "");
        binding.variable = binding.variable.replaceAll("}", "");

        if (binding.name === "") {
          return;
        }

        const [source, field] = binding.value?.split(".") ?? [];

        const existInDefaultVariables = variables.findIndex((variable) => variable.name === binding.name);
        if (existInDefaultVariables >= 0) {
          variables[existInDefaultVariables].value = binding.value;

          if (binding.origin === OriginVariables.EVENT) {
            variables[existInDefaultVariables]._value = { source, field };
          }
        } else {
          if (binding.origin === OriginVariables.EVENT) {
            binding.value = binding.value || "";
            binding._value = { source, field };
          }

          variables.push(binding);
        }
      });

      variables.forEach((variable) => this.templateEditorStore.addVariable(variable));
    } else {
      this.defaultVariables.forEach((variable) => this.templateEditorStore.addVariable(variable));
    }

    void this.loadTemplate();
  }

  *loadTemplate() {
    if (!this.template) return;
    try {
      this.isLoading = true;

      const response = yield API.graphql({
        query: getUrlFile,
        variables: {
          input: {
            customer: this.customer,
            file_name: `${this.template}`,
            file_route: this.s3FileRoute,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const uploadUrl = JSON.parse(response.data.getUrlFile?.body);

      const res = yield axios({
        url: uploadUrl,
        method: "GET",
        responseType: "blob",
      });
      const blobRes = new Blob([res.data]);
      const streamer = yield blobRes.text();

      this.templateJson = JSON.parse(streamer);
      this.templateEditorStore.updateField('design', this.templateJson);
    } catch (error) {
      this.templateJson = {};
    } finally {
      this.isLoading = false;
    }
  }

  *uploadFileToS3(content, type, name, file_route=null) {
    const blobFile = new Blob([content], {type});

    const response = yield API.graphql({
      query: getUploadUrlFile,
      variables: {
        input: {
          customer: this.customer,
          file_name: name,
          file_type: type,
          file_route: file_route || this.s3FileRoute,
          overwrite: true,
        },
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });

    const uploadUrl = JSON.parse(response.data.getUploadUrlFile?.body);
    const config = {
      headers: {
        "Content-Type": type,
      },
    };

    yield axios.put(uploadUrl, blobFile, config);
    return uploadUrl;
  }

  *saveTemplate(html, json) {
    try {
      this.updatingTemplate = true;
      this.isError = false;

      if (json) {
        yield this.uploadFileToS3(
          JSON.stringify(json, null, 2),
          "application/json",
          `${this.id}.json`
        ); 
      }

      if (html) {
        yield this.uploadFileToS3(html, "text/html", `${this.id}.html`);
      }
    } catch (error) {
      this.isError = true;
    } finally {
      this.updatingTemplate = false;
    }
  }

  clearTemplate() {
    this.isError = false;
    this.isLoading = false;
    this.updatingTemplate = false;
    this.s3FileRoute = "email_templates";
    this.template = "";
    this.body = "";
    this.variables = [];
    this.payload = {};
    this.templateJson = null;

    this.templateEditorStore.clear();
  }
}

export default MessageStore;
